/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-* are new internal custom properties defined here, not design tokens */
import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars } from '#utils/styles'

/**
 * Shared shimmer background + animation for component-owned `loading`
 * states — a highlight band sweeping over a static base color, rather than
 * cross-fading between two colors.
 *
 * The band's width is `max(<floor>, <percentage>)` of the element it's
 * applied to (`--lfds-skeleton-band-width`, default `max(48px, 80%)`): the
 * percentage keeps a wide bar (e.g. a full-width image placeholder)
 * proportionally wide, while the floor keeps a narrow one (a checkbox/radio
 * box, an icon) from shrinking to an imperceptible sliver — small elements
 * end up mostly/fully covered by the band and read as a soft flash instead,
 * same as the fixed-width band this replaced did for small elements.
 * Override `--lfds-skeleton-band-width` locally if a specific shape needs a
 * different floor/proportion.
 *
 * Import this into a component's `static styles` array and reference
 * `background: var(--lfds-skeleton-background); animation: var(--lfds-skeleton-animation);`
 * from the component's own selectors — see `lfds-input.css.ts` or
 * `lfds-list-row-content.css.ts` for usage.
 *
 * Skeleton surfaces are typically sized to fill their container exactly
 * (`inset: 0`), so adjacent bars would otherwise touch and visually fuse
 * into one shape. Pair with `border: var(--lfds-skeleton-border);
 * background-clip: padding-box;` (or `content-box` when masking real
 * content in place) — the transparent border reveals a thin gap without
 * changing the element's outer bounds (no layout/sizing impact).
 *
 * `--lfds-skeleton-border-radius` defaults to `border-radius-xs` — use it
 * instead of repeating the token so every bar/mask stays in sync, and
 * override locally only when a shape needs something else (e.g. a whole
 * field box keeping its own field radius, or a circular avatar mask).
 *
 * The sweep's start/end `background-position` travel by
 * `max(300px, 450%)`, not a value derived from `--lfds-skeleton-band-width`:
 * background-position percentages resolve against (container size -
 * background size), not the container alone, so reusing a value with a
 * percentage nested inside it (like `--lfds-skeleton-band-width`) re-resolves
 * that percentage against the wrong base and under-shoots — worse, for an
 * element narrower than the floor (48px), that base goes negative, which
 * flips the sweep's apparent direction into reverse. `max(300px, 450%)`
 * sidesteps both problems: the flat 300px always wins for small/negative
 * bases, guaranteeing the sweep starts fully to the left (never backwards,
 * never frozen with no room to move), while the 450% term takes over and
 * keeps scaling for wide elements instead of capping out at a fixed pixel
 * amount that would eventually be too small for a very wide band. 450% is
 * tuned for the current 80% band width — if that default changes
 * meaningfully, recompute as `>= 100 / (1 - <percentage>)`.
 */
export default css`
	:host {
		--lfds-skeleton-band-width: max(48px, 80%);
		--lfds-skeleton-background: ${vars(tokens.color_background_skeleton_bg)}
			linear-gradient(
				270deg,
				${vars(tokens.color_background_skeleton_shimmer_0)} 0%,
				${vars(tokens.color_background_skeleton_shimmer_100)} 50%,
				${vars(tokens.color_background_skeleton_shimmer_0)} 100%
			)
			calc(-1 * max(300px, 450%)) 0 / var(--lfds-skeleton-band-width) 100%
			no-repeat;
		--lfds-skeleton-animation: lfds-skeleton-shimmer-sweep 2000ms linear 200ms
			infinite;
		--lfds-skeleton-border: 2px solid transparent;
		--lfds-skeleton-border-radius: ${vars(tokens.border_radius_xs)};
	}

	@keyframes lfds-skeleton-shimmer-sweep {
		to {
			background-position: calc(100% + max(300px, 450%)) 0;
		}
	}

	/*
	 * Centralized here so consuming components don't each need their own
	 * reduced-motion override: falls back to the flat base color, no sweep.
	 */
	@media (prefers-reduced-motion: reduce) {
		:host {
			--lfds-skeleton-background: ${vars(tokens.color_background_skeleton_bg)};
			--lfds-skeleton-animation: none;
		}
	}
`
